import { useEffect, useState } from "react";
import { ArrowUpRight, FileText, FolderOpen, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import StrategyRoomCTA from "@/components/workspace/StrategyRoomCTA";
import { formatVerifiedMetric, isSeedMetricSet, metricsAreVerified, profileKind } from "@/lib/businessIntegrity";

export default function Dashboard({ lang, t, data, setData, setSection }) {
  const b = data.business || {};
  const m = b.metrics || {};
  const fa = lang === "fa";
  const counts = data.counts || {};
  const kind = profileKind(b);
  const verified = metricsAreVerified(b);
  const [events, setEvents] = useState(null);
  const clear = async () => { await api.delete("/business/sample-data"); setData((d) => ({ ...d, business: { ...d.business, metrics: {}, sample_data: false } })); };
  const metrics = [
    ["revenue", fa ? "درآمد" : "Revenue"],
    ["orders", fa ? "سفارش‌ها" : "Orders"],
    ["customers", fa ? "مشتریان" : "Customers"],
    ["conversion", fa ? "نرخ تبدیل" : "Conversion"],
  ];
  const shortcuts = [["strategy", Sparkles, t.strategy, counts.strategy], ["files", FolderOpen, t.files, counts.files], ["reports", FileText, t.reports, counts.reports]];

  useEffect(() => {
    let active = true;
    api.get("/business/timeline").then((result) => { if (active) setEvents(result.data || []); }).catch(() => { if (active) setEvents([]); });
    return () => { active = false; };
  }, [b.business_id]);

  return (
    <div className="dashboard">
      <section className="strategy-invite" data-testid="dashboard-strategy-invite">
        <div>
          <p className="eyebrow">NOORIN</p>
          <h2>{t.startConversation}</h2>
          <p>{t.startConversationHint}</p>
        </div>
        <StrategyRoomCTA t={t} prominent testId="strategy-room-cta" />
      </section>
      <section className="welcome-band" data-profile-kind={kind}>
        <div>
          <span className="status-dot"></span>
          <span>{kind === "demonstration" ? t.demoProfile : kind === "missing" ? t.missingProfile : t.customerProfile}</span>
          <h2>{kind === "missing" ? t.enterBusiness : b.business_name}</h2>
          <p>{kind === "demonstration" ? t.demoProfileNote : kind === "missing" ? t.missingProfileNote : (b.description || t.missingProfileNote)}</p>
        </div>
        <div className="row-actions">
          {b.sample_data && <button className="quiet-button" onClick={clear} data-testid="clear-sample-data-button">{t.clear}</button>}
          <button className="quiet-button" onClick={() => setSection("business")} data-testid="dashboard-edit-business">{kind === "customer" ? t.edit : t.confirmBusiness}</button>
        </div>
      </section>
      <div className="focus-grid">
        <div className="focus-panel"><p className="eyebrow">{t.focus}</p><h2>{t.focusText}</h2><div className="focus-line"><span>01</span><span>{data.subscription ? `${data.subscription.package} · ${fa ? "فعال" : "active"}` : (fa ? "بدون پلن فعال" : "No active plan")}</span></div></div>
        <div className="next-panel"><p className="eyebrow">{t.next}</p><h3>{t.nextText}</h3><button className="circle-arrow" onClick={() => setSection("strategy")} data-testid="dashboard-next-step-button" aria-label={t.startConversation}><ArrowUpRight size={18} /></button></div>
      </div>
      <div className="section-heading"><div><p className="eyebrow">{t.snapshot}</p><h2>{t.signals}</h2></div><span className="timestamp">{t.updated} · {b.updated_at ? new Date(b.updated_at).toLocaleDateString(fa ? "fa-IR" : "en-GB") : ""}</span></div>
      {isSeedMetricSet(m) && (
        <details className="demo-preview" data-testid="demo-metrics-preview">
          <summary>{t.demoMetricsTitle}</summary>
          <p>{t.demoMetricsNote}</p>
          <ul>
            <li>{fa ? "درآمد ذخیره‌شده" : "Stored revenue"}: {Number(m.revenue || 0).toLocaleString(fa ? "fa-IR" : "en-GB")}</li>
            <li>{fa ? "سفارش" : "Orders"}: {m.orders ?? "—"}</li>
            <li>{fa ? "مشتری" : "Customers"}: {m.customers ?? "—"}</li>
            <li>{fa ? "نرخ تبدیل" : "Conversion"}: {m.conversion ?? "—"}%</li>
          </ul>
        </details>
      )}
      {!verified && kind !== "demonstration" && !isSeedMetricSet(m) && m && Object.keys(m).length > 0 && <p className="source-note">{t.unverifiedMetrics}</p>}
      <div className="metrics">{metrics.map(([key, label]) => {
        const value = verified ? formatVerifiedMetric(key, m, lang) : null;
        return <div className={`metric ${value ? "" : "pending"}`} key={key} data-testid={`metric-${key}`} data-verified={value ? "true" : "false"}><span>{label}</span><strong>{value ?? t.noData}</strong><small>{value ? (fa ? "تأییدشده" : "Confirmed") : ""}</small></div>;
      })}</div>
      <div className="shortcuts">{shortcuts.map(([key, Icon, label, count]) => <button className="shortcut" key={key} onClick={() => setSection(key)} data-testid={`dashboard-shortcut-${key}`}><Icon size={18} /><span>{label}</span><strong>{count ?? 0}</strong></button>)}</div>
      <div className="activity">
        <div>
          <p className="eyebrow">{t.recent}</p>
          <p className="source-note">{t.activitySource}</p>
          {events === null ? <p className="muted">{t.loading}</p> : events.length === 0 ? <div className="activity-empty" data-testid="activity-empty"><p>{t.activityEmpty}</p></div> : events.slice(0, 4).map((event, index) => (
            <div className="activity-row" key={`${event.created_at}-${index}`}><div><strong>{event.summary}</strong><small>{event.created_at ? new Date(event.created_at).toLocaleString(fa ? "fa-IR" : "en-GB") : ""}{event.source ? ` · ${event.source}` : ""}</small></div></div>
          ))}
        </div>
        <div className="quote">“{t.principleQuote}”<span>— {t.principle}</span></div>
      </div>
    </div>
  );
}
